import { calcClientDemographics } from '@shared';
import type { PaymentOption } from '@/features/shop-insurance';

const POLICY_TYPE_BY_TAB: Record<ShopInsuranceTab, string> = {
  single: 'Single Life',
  joint: 'Joint Life',
};

interface BuildQuoteQueryPayloadArgs {
  client?: Client | null;
  clientPartner?: Client | null;
  policyType: ShopInsuranceTab;
  hasPartnerWithDemographics: boolean;
  nycLifeDemo?: boolean;
  /**
   * Omit to have the server return every payment option in one call — what benefit-design mode
   * needs to group quotes by pay structure.
   */
  cobPaymentOption?: PaymentOption;
}

/**
 * Single source of truth for the Shop Insurance quote request, shared by the premium-optimization
 * and benefit-design fetching hooks.
 *
 * Pure by design: both callers derive a stable key from the result via `quoteQueryKey` and refetch
 * only when that key changes. Mutations such as selecting a product replace `state.client.data`
 * with a new object reference while leaving every quote input untouched, and refetching there
 * clears the quote list and throws the shopper back to the top of the page.
 */
export function buildQuoteQueryPayload({
  client,
  clientPartner,
  policyType,
  hasPartnerWithDemographics,
  nycLifeDemo = false,
  cobPaymentOption,
}: BuildQuoteQueryPayloadArgs): FetchQuotesPayload | null {
  if (!client?.intakeSurvey) {
    return null;
  }

  const { primaryAge, primaryGender, primaryTobaccoStatus, state } = calcClientDemographics(
    client.intakeSurvey,
  );

  const demographics: FetchQuotesDemographics = {
    primaryAge,
    primaryGender,
    primaryTobaccoStatus,
    usaState:
      nycLifeDemo && state?.stateAbbreviation === 'NY' ? 'NJ' : (state?.stateAbbreviation ?? ''),
    policyType: POLICY_TYPE_BY_TAB[policyType],
    maritalStatus: client.intakeSurvey.clientMaritalStatus,
    hasPartnerWithDemographics,
    predictedCareStartAge: client.appliedInferenceSet?.ltcAtAge,
  };

  if (cobPaymentOption) {
    demographics.cobPaymentOption = cobPaymentOption;
  }

  if (policyType === 'joint' && hasPartnerWithDemographics && clientPartner?.intakeSurvey) {
    const partner = calcClientDemographics(clientPartner.intakeSurvey);
    demographics.secondaryAge = partner.primaryAge;
    demographics.secondaryGender = partner.primaryGender;
    demographics.secondaryTobaccoStatus = partner.primaryTobaccoStatus;
  }

  return { demographics, advisorId: client.advisorId };
}

/** Stable request identity both fetching hooks key their auto-fetch effects on. */
export function quoteQueryKey(payload: FetchQuotesPayload | null): string | null {
  return payload && JSON.stringify(payload);
}
